/**
 * Все слова просмотра картинок крупно (docs/homework-image-viewer-plan.md, Р6 и
 * Р9), строками и без пакета.
 *
 * ⚠️ Модуль пакета `yet-another-react-lightbox` не знает и знать не должен:
 * его импортируют форма выдачи (стартовый чанк), лента и фото решения, а пакет
 * живёт только в `ImageViewer.tsx` за `import()`. Подписи пакета поэтому лежат
 * здесь обычным объектом, а сверку с типом `Labels` делает `ImageViewer.tsx`
 * (разность ключей обязана быть пустой). Замок `src/image-viewer-barrier.test.ts`.
 *
 * `IMAGE_FAILED` и `imageLabel` переехали сюда из `exercises/homework-done.ts`:
 * их читают и лента, и слой, и форма, а форма в стартовом чанке не должна
 * тянуть модуль ленты.
 */

export const IMAGE_FAILED = "Картинка не загрузилась.";

/** Имя картинки для читалки: у двадцати одинаковых «картинка» не различить. */
export function imageLabel(index: number, total: number): string {
  return total === 1 ? "Картинка к домашке" : `Картинка ${index + 1} из ${total}`;
}

/** Имя фото решения в слое. Одно фото называется так же, как называлось в окне до слоя. */
export function photoLabel(index: number, total: number): string {
  return total === 1 ? "Фотография решения" : `Фотография ${index + 1} из ${total}`;
}

/** Подпись кнопки плитки: «Картинка 2 из 5: открыть крупно». */
export function openLabel(label: string): string {
  return `${label}: открыть крупно`;
}

/** Вид медиа в слое: видео или аудио материала формы выдачи. */
export type ViewerMediaKind = "video" | "audio";

/**
 * Подпись кнопки плитки медиа: «Видео «Имя»: смотреть», «Аудио «Имя»: слушать»
 * (docs/homework-media-form-player-plan.md, Р6). Имя собирает вызывающий.
 */
export function playLabel(kind: ViewerMediaKind, label: string): string {
  return `${label}: ${kind === "video" ? "смотреть" : "слушать"}`;
}

/** Разделитель счётчика слоя: «2 из 5». Пробелы вокруг ставит пакет. */
export const COUNTER_SEPARATOR = "из";

/** Повтор загрузки слайда, тем же словом, что в ленте. */
export const RETRY = "Повторить";

/** Какие слова у слоя: картинка материала или фотография решения. */
export type ViewerNoun = "image" | "photo";

export interface ViewerWords {
  /** Чанк просмотрщика ещё едет. */
  opening: string;
  /** Адрес картинки слайда ещё не пришёл. */
  loading: string;
  /** Картинка слайда не загрузилась и после перечитывания. */
  failed: string;
  /** Чанк просмотрщика не загрузился: обрыв или выкатка. */
  openFailed: string;
}

/**
 * Отказ открытия у картинки. Отдельным именем, потому что лента зовёт его до
 * переезда на слой (шаг 3 плана убирает ту ветку целиком).
 */
export const VIEWER_OPEN_FAILED =
  "Не получилось открыть картинку. Проверьте связь и нажмите на неё ещё раз.";

/**
 * Слова по виду. «На неё» одинаково про картинку и про фотографию (обе женского
 * рода), поэтому фразы общие по форме, а не склеены из кусков.
 */
export const VIEWER_WORDS: Readonly<Record<ViewerNoun, ViewerWords>> = {
  image: {
    opening: "Открываем картинку",
    loading: "Загружаем картинку",
    failed: IMAGE_FAILED,
    openFailed: VIEWER_OPEN_FAILED,
  },
  photo: {
    opening: "Открываем фотографию",
    loading: "Загружаем фотографию",
    failed: "Фотография не загрузилась.",
    openFailed: "Не получилось открыть фотографию. Проверьте связь и нажмите на неё ещё раз.",
  },
};

/**
 * Слова медиа. «Аудио» одно слово везде, средний род, как и «видео»: поэтому
 * обе фразы отказа кончаются на «на него».
 */
export const VIEWER_MEDIA_WORDS: Readonly<Record<ViewerMediaKind, ViewerWords>> = {
  video: {
    opening: "Открываем видео",
    loading: "Загружаем видео",
    failed: "Видео не загрузилось.",
    openFailed: "Не получилось открыть видео. Проверьте связь и нажмите на него ещё раз.",
  },
  audio: {
    opening: "Открываем аудио",
    loading: "Загружаем аудио",
    failed: "Аудио не загрузилось.",
    openFailed: "Не получилось открыть аудио. Проверьте связь и нажмите на него ещё раз.",
  },
};

/** Слова слайда или открытия: у медиа по его виду, иначе по виду слоя. */
export function viewerWordsOf(noun: ViewerNoun, media: ViewerMediaKind | undefined): ViewerWords {
  return media === undefined ? VIEWER_WORDS[noun] : VIEWER_MEDIA_WORDS[media];
}

/**
 * Подписи пакета по-русски, все десять: восемь ядра и две плагина Zoom.
 * Других строк для человека в пакете нет (проверено по `dist/` версии 3.32.2).
 * У фото решения подписи те же: «Предыдущая картинка» там неточно, но это
 * подпись кнопки для читалки, а второй объект ради неё не стоит своей цены
 * (Р9 плана).
 *
 * ⚠️ Лишний и опечатанный ключ ловит компиляция `ImageViewer.tsx` (разность
 * ключей этого объекта и `Labels` пакета обязана быть пустой). Пропущенный она
 * не ловит: у пакета все ключи необязательные, и пропущенный молча останется
 * английским. Полноту сверяет `viewer-model.test.ts`.
 */
export const VIEWER_LABELS = {
  Previous: "Предыдущая картинка",
  Next: "Следующая картинка",
  Close: "Закрыть",
  "Zoom in": "Увеличить",
  "Zoom out": "Уменьшить",
  Lightbox: "Просмотр картинок",
  "Photo gallery": "Картинки",
  // Описания ролей: читалка говорит их после имени («Картинки, лента картинок»)
  Carousel: "лента картинок",
  Slide: "картинка",
  "{index} of {total}": "{index} из {total}",
} as const;

/**
 * Подписи пакета, когда в слое есть видео или аудио (выбор `labelSetOf` в
 * `viewer-model.ts`): листаются уже файлы, а не картинки. Ключи те же десять, и
 * сверка та же: лишний ловит `ImageViewer.tsx`, пропущенный `viewer-model.test.ts`.
 */
export const VIEWER_LABELS_MIXED = {
  Previous: "Предыдущий файл",
  Next: "Следующий файл",
  Close: "Закрыть",
  "Zoom in": "Увеличить",
  "Zoom out": "Уменьшить",
  Lightbox: "Просмотр материала",
  "Photo gallery": "Материал",
  // Описания ролей: «Материал, лента материала»
  Carousel: "лента материала",
  Slide: "файл",
  "{index} of {total}": "{index} из {total}",
} as const;
